import { Tooltip, TooltipProps } from "@mui/material";
import React, { ReactNode } from "react";

type ConditionalWrapperProps = {
	/** Boolean condition to determine which wrapper to use (true -> wrapper / false -> fallbackWrapper) */
	condition: boolean;
	/** Use whenTrue function when condition is true */
	whenTrue: (children: ReactNode) => ReactNode;
	/** Use whenFalse function when condition is false */
	whenFalse?: (children: ReactNode) => ReactNode;
	/** Content to be wrapped */
	children: ReactNode;
};

export const ConditionalWrapper = ({ condition, whenTrue, whenFalse, children }: ConditionalWrapperProps) => {
	if (condition) return whenTrue(children);

	if (whenFalse) return whenFalse(children);

	return children;
};

type TooltipWrapperProps = Pick<ConditionalWrapperProps, "condition" | "children"> & {
	title: React.ReactNode;
	/**If the children should be wrapped in a span for tooltip.
	 * This is because MUI requires single element and ConditionalWrapper returns ReactNode which could be multiple elements.
	 * Also if children is disabled, should wrap in span, because disabled elements won't receive pointer events
	 * @default true */
	wrapInSpan?: boolean;
} & Omit<TooltipProps, "title" | "children">;

export function TooltipWrapper({ children, condition, title, wrapInSpan = true, ...props }: TooltipWrapperProps) {
	return (
		<ConditionalWrapper
			condition={condition}
			whenTrue={(children) => {
				const isSingleElement = React.isValidElement(children);

				// If they asked not to wrap, but it's not a single element, wrap to prevent MUI crashing.
				const shouldWrap = wrapInSpan || !isSingleElement;
				return (
					<Tooltip {...props} title={title}>
						{shouldWrap ? <span>{children}</span> : children}
					</Tooltip>
				);
			}}
		>
			{children}
		</ConditionalWrapper>
	);
}
